import { handlePdeRelay, validatePdeNonce } from '@aecom-ai-engineering/auth';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isPdeEnvironment } from './config';
import { getSavedPath, getTokenFromCode, setSessionInStorage } from './oauth';
import { useAuthContext } from './react-context';
const useQueryParam = (param) => {
    const { search } = useLocation();
    return useMemo(() => {
        const params = new URLSearchParams(search);
        return params.get(param);
    }, [search, param]);
};
/**
 * Consume an OAuth `?code=` from the URL and exchange it for a session.
 *
 * Used by both the dedicated `/auth` callback route and `AuthGuard` so that
 * the token exchange can happen at whichever path B2C (or the PDE relay)
 * forwards back to. Since `@aecom-ai-engineering/auth` 0.0.14, the relay
 * forwards to the PDE-side path the login was initiated from rather than to
 * the relay's own pathname, so the callback handler must be available on
 * every protected route, not just `/auth`.
 */
export const useOAuthCallback = ({ authConfig, destination, }) => {
    const code = useQueryParam('code');
    const navigate = useNavigate();
    const { setSession } = useAuthContext();
    // Guard against double-invocation of the callback effect. The OAuth code,
    // PDE nonce, and PKCE verifier are all single-use: the first run consumes
    // them, so any second run (StrictMode, prop reference churn, route
    // re-mount, etc.) would fail validation and navigate("/"), which unmounts
    // this component and aborts the in-flight token exchange. The ref persists
    // across re-renders for the same component instance, so once we've started
    // handling a given code we ignore subsequent effect invocations for it.
    const handledCodeRef = useRef(null);
    const handleTokens = useCallback((session) => {
        setSession(session);
        /** Store auth data in session storage */
        setSessionInStorage(session);
        const savedPath = getSavedPath();
        if (savedPath) {
            navigate(savedPath);
        }
        else {
            navigate(destination);
        }
    }, [destination, navigate, setSession]);
    useEffect(() => {
        if (!code) {
            return;
        }
        if (handledCodeRef.current === code) {
            return;
        }
        handledCodeRef.current = code;
        const handleCallback = async () => {
            // Check whether this callback is running on the stable PDE auth relay.
            if (handlePdeRelay(authConfig)) {
                // Browser is being redirected to PDE origin, no further action needed
                return;
            }
            // If on PDE, validate nonce before exchanging tokens
            if (isPdeEnvironment()) {
                const state = new URLSearchParams(window.location.search).get('state');
                const nonceValid = validatePdeNonce(state);
                if (!nonceValid) {
                    console.error('PDE nonce validation failed - possible CSRF attack');
                    navigate('/', { replace: true });
                    return;
                }
            }
            // Exchange code for tokens
            try {
                const session = await getTokenFromCode(authConfig, code);
                handleTokens(session);
            }
            catch (error) {
                console.error('Token exchange failed:', error);
                navigate('/', { replace: true });
            }
        };
        handleCallback();
    }, [authConfig, code, handleTokens, navigate]);
    return { isHandlingCallback: !!code };
};
